// web/src/components/ds/ds-icon-aliases.ts — DS.5 (sprint aos-ds-shell, spec §2 D6).
//
// The Material→Lucide alias table: every icon NAME a vendored DS piece can emit
// (Material Symbols snake_case, the vocabulary `@ds/primitives/Icon` receives)
// mapped to a key of `atoms/Icon.tsx`'s `iconMap` (Lucide PascalCase, the app's
// one icon registry). `IconFamilyLucide.tsx` resolves in two hops — this table,
// then `iconMap` — and THROWS on a name absent here (D6: prevention, not a
// fallback; two pieces take a consumer-supplied name where no grep can see a
// gap). The registry is `iconMap`, never duplicated here: every value must be
// an `IconName`, which the type below enforces at compile time.
//
// OWNERSHIP (D6, §6 row 12): born COMPLETE for the landed tag by DS.5;
// DS.7/DS.8 EXTEND rows only when a later vendor tag adds a name; nobody
// creates a second table. `atoms/Icon.tsx` (owner DS.9) is the only place a
// NEW Lucide glyph can be registered — rows marked `approx` below map a
// Material name whose exact Lucide analogue is not in the registry to the
// nearest registered glyph; DS.9 extends `iconMap` when a wave mounts a piece
// that draws one of them for the founder.
//
// DERIVATION at `vendor/venuz-design` v0.2.0 (`git subtree` trailer at
// dfc98b5b), `shared/` pruned (DS.4 D2) — spec §0.11b / AC-18, re-run at open
// 2026-09-14, `[medido]`:
//   (i)  span literals `material-symbols-rounded…>name`        → 0 (DS.3b PR-3 moved every span to <Icon>)
//   (ii) `<Icon name="…">`                                     → 41
//   (iv) `<Glyph name="…">` (4 local helpers)                  → 21 (15 outside (ii))
//   NavArrow `ICONS` table                                     → 4 (2 outside (ii))
//   union of the greppable sources                             → 58
//   expression sites, read piece by piece (ledger below)       → +38
//   total (vendor)                                             → 96 (floor: AC-18 ≥ 70)
//   + 6 As Oasis-side names the shell call sites use            → 102 rows
//
// AC-18 reads the expression-site names back from the `# expression-site:` lines
// below (column-0 inside this block comment, one per line, exactly as the
// criterion's `sed` expects) and derives the greppable set from the vendor tree
// itself, so the criterion cannot drift from the table.
/*
# expression-site: add_photo_alternate
# expression-site: alternate_email
# expression-site: arrow_downward
# expression-site: arrow_upward
# expression-site: autorenew
# expression-site: card_membership
# expression-site: check_circle
# expression-site: description
# expression-site: draft
# expression-site: error
# expression-site: expand_less
# expression-site: flight
# expression-site: fullscreen
# expression-site: fullscreen_exit
# expression-site: image
# expression-site: keep_off
# expression-site: keyboard_arrow_up
# expression-site: lightbulb
# expression-site: local_fire_department
# expression-site: lock_open
# expression-site: person_add
# expression-site: pets
# expression-site: photo_library
# expression-site: picture_as_pdf
# expression-site: restaurant
# expression-site: settings
# expression-site: sign_language
# expression-site: sports_soccer
# expression-site: sync_disabled
# expression-site: table_chart
# expression-site: tag
# expression-site: trending_down
# expression-site: trending_flat
# expression-site: trending_up
# expression-site: unfold_more
# expression-site: visibility_off
# expression-site: volunteer_activism
# expression-site: warning

Where each expression-site name comes from (v0.2.0, read by hand):
  feedback/Alert.tsx + feedback/Toast.tsx `typeIcons`   → check_circle warning error (info is in (ii))
  data-display/{DataGrid,Table}.tsx `getSortIcon`         → unfold_more arrow_upward arrow_downward
  content-blocks/KpiCard.tsx `trendIcon`                  → trending_up trending_down trending_flat
                                                            (+ the consumer-supplied `icon` prop)
  content-blocks/emojiData.ts category `icon`             → sign_language pets restaurant sports_soccer
                                                            flight lightbulb tag (mood favorite person schedule in (ii)/(iv))
  data-input/FileUpload.tsx file-type table               → picture_as_pdf description table_chart image draft
  content-blocks/MapContainer.tsx                         → fullscreen fullscreen_exit
  data-input/DatePicker.tsx                               → expand_less
  content-blocks/MessageThread.tsx revenue/pill tables    → lock_open volunteer_activism card_membership
                                                            autorenew keep_off visibility_off (ternaries,
                                                            also content-blocks/PaywallMediaSplit.tsx)
  content-blocks/FanList.tsx status table                 → sync_disabled local_fire_department person_add
  content-blocks/RichTextEditor.tsx toolbar array         → add_photo_alternate photo_library alternate_email
                                                            keyboard_arrow_up
  navigation/WebTopNav.tsx `<NavGlyph name="settings">`   → settings (a 5th local helper the `<Glyph>` grep
                                                            cannot see; notifications is in (iv))
  navigation/WebTopNav.tsx `entry.icon` / `item.icon`,
  content-blocks/PaywallMediaSplit.tsx `icon`             → consumer-supplied (the throw is the detector)
*/

import type { IconName } from '../atoms/Icon';

// As Oasis shell call sites reach DS `Icon` with these names too (not emitted
// by any vendor piece; listed so the shell never depends on an approx row).
export const DS_ICON_ALIASES: Record<string, IconName> = {
  add: 'Plus',
  add_photo_alternate: 'Image', // approx
  alternate_email: 'Globe', // approx
  archive: 'Archive',
  arrow_back: 'ChevronLeft', // approx: ArrowLeft not in the registry
  arrow_downward: 'ArrowDown',
  arrow_forward: 'ChevronRight', // approx: ArrowRight not in the registry
  arrow_upward: 'ArrowUp',
  attach_file: 'Paperclip',
  autorenew: 'RefreshCw',
  broken_image: 'Image', // approx
  calendar_today: 'Clock', // approx
  campaign: 'Bell', // approx: Megaphone not in the registry
  cancel: 'XCircle',
  card_membership: 'ShieldCheck', // approx
  check: 'Check',
  check_circle: 'CircleCheck',
  chevron_left: 'ChevronLeft',
  chevron_right: 'ChevronRight',
  close: 'X',
  cloud_upload: 'FilePlus', // approx: Upload not in the registry
  content_copy: 'Copy',
  dark_mode: 'Moon',
  delete: 'Trash2',
  deployed_code: 'Square', // approx: the WebTopNav brand mark
  description: 'FileText',
  dns: 'Server',
  done_all: 'ListChecks', // approx: CheckCheck not in the registry
  draft: 'FileText', // approx
  drag_indicator: 'MoreVertical', // approx: GripVertical not in the registry
  edit: 'Pencil',
  error: 'CircleAlert',
  expand_less: 'ArrowUp', // approx: ChevronUp not in the registry
  expand_more: 'ChevronDown',
  favorite: 'Sparkles', // approx: Heart not in the registry
  filter_list: 'ListChecks', // approx: Filter not in the registry
  flight: 'CornerUpRight', // approx
  format_bold: 'Pencil', // approx
  format_italic: 'Pencil', // approx
  forum: 'Bot', // approx: MessageSquare not in the registry
  fullscreen: 'Square', // approx: Maximize not in the registry
  fullscreen_exit: 'Square', // approx: Minimize not in the registry
  gif_box: 'Image',
  image: 'Image',
  info: 'HelpCircle', // approx: Info not in the registry
  ink_eraser: 'Trash2', // approx
  keep_off: 'PinOff',
  keyboard_arrow_down: 'ChevronDown',
  keyboard_arrow_up: 'ArrowUp', // approx: ChevronUp not in the registry
  light_mode: 'Sun',
  lightbulb: 'Zap', // approx
  local_fire_department: 'Zap', // approx
  location_on: 'Map',
  lock: 'ShieldCheck', // approx: Lock not in the registry
  lock_open: 'ShieldQuestion', // approx
  logout: 'LogOut',
  manage_accounts: 'Settings',
  map: 'Map',
  match_case: 'FileText', // approx
  menu: 'Menu',
  mood: 'Sparkles', // approx: Smile not in the registry
  more_horiz: 'MoreHorizontal',
  more_vert: 'MoreVertical',
  notifications: 'Bell',
  notifications_off: 'Bell', // approx: BellOff not in the registry
  payments: 'BarChart3', // approx
  person: 'CircleUser',
  person_add: 'CircleUser', // approx
  pets: 'Sparkles', // approx
  photo_library: 'Files', // approx
  picture_as_pdf: 'FileText', // approx
  play_arrow: 'Play',
  push_pin: 'Pin',
  refresh: 'RefreshCw',
  remove: 'X', // approx: Minus not in the registry
  reply: 'CornerUpRight',
  restaurant: 'Sparkles', // approx
  restore: 'RotateCw',
  rotate_left: 'RotateCcw',
  rotate_right: 'RotateCw',
  schedule: 'Clock',
  search: 'Search',
  sell: 'Zap', // approx: Tag not in the registry
  send: 'CornerUpRight', // approx: Send not in the registry
  sentiment_dissatisfied: 'AlertCircle', // approx
  settings: 'Settings',
  sign_language: 'CircleUser', // approx
  sports_soccer: 'Activity', // approx
  swap_horiz: 'ArrowRightLeft',
  sync_disabled: 'WifiOff', // approx
  table_chart: 'BarChart3', // approx
  tag: 'Zap', // approx
  translate: 'Globe',
  trending_down: 'ArrowDown', // approx
  trending_flat: 'ArrowRightLeft', // approx
  trending_up: 'ArrowUp', // approx
  unfold_more: 'ChevronDown', // approx: ChevronsUpDown not in the registry
  verified: 'ShieldCheck',
  visibility: 'Eye',
  visibility_off: 'EyeOff',
  volunteer_activism: 'Sparkles', // approx
  warning: 'AlertTriangle',
};
